import type { ComponentType, SVGProps } from "react";

import { cx } from "@/lib/cx";

/**
 * EXPLORATORY SPIKE (2026-08-27) — see `index.css`'s own `--color-vibrant-*`
 * comment. DESIGN.md's real spec for this component today is plainer: "no
 * icons... hairline border" — this is a live demonstration of a bolder
 * alternative the Owner asked to see, not yet the committed shape. `icon`/
 * `tint` are optional specifically so a caller that doesn't pass them still
 * renders the original, unmodified plain tile — nothing already using this
 * component broke to build this.
 *
 * `"danger"` (Owner-requested, 2026-08-28): the one tint that is semantic
 * rather than decorative — reuses the app's real, load-bearing
 * `--color-danger`/`--color-danger-quiet` tokens (the same ones every error
 * message already renders with), never one of the `--color-vibrant-*`
 * decorative hues above. Unlike every other tint, it also darkens the
 * tile's own value text to `text-danger` (not just the icon badge) — the
 * Home dashboard's own "the backup is stale" card needs the number itself
 * to read as alarmed at a glance, not just a small icon in the corner.
 */
export interface StatTileProps {
  value: string;
  label: string;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  tint?: "indigo" | "cyan" | "violet" | "amber" | "danger";
}

const TINT_CLASSES: Record<NonNullable<StatTileProps["tint"]>, string> = {
  indigo: "bg-vibrant-indigo-quiet text-vibrant-indigo",
  cyan: "bg-vibrant-cyan-quiet text-vibrant-cyan",
  violet: "bg-vibrant-violet-quiet text-vibrant-violet",
  amber: "bg-vibrant-amber-quiet text-vibrant-amber",
  danger: "bg-danger-quiet text-danger",
};

export function StatTile({ value, label, icon: Icon, tint = "indigo" }: StatTileProps) {
  return (
    <div className="rounded-lg bg-surface p-5 shadow-card">
      {Icon ? (
        <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-md ${TINT_CLASSES[tint]}`}>
          <Icon width={20} height={20} />
        </div>
      ) : null}
      <p className={cx("numeric-features text-title font-semibold", tint === "danger" ? "text-danger" : "text-ink")}>
        {value}
      </p>
      <p className="mt-1 text-meta text-ink-secondary">{label}</p>
    </div>
  );
}
